import type { components } from "@contracts/generated/editor-service";

import {
  OMISSION_REASONS,
  OMISSION_REASON_LABELS,
  previewRunPath,
  type MissingRange,
  type OmissionReason,
  type TraceCompleteness,
} from "./previewInsights";

/**
 * Flow & Decisions — the run page's decision timeline, read from the trace's
 * own events (#1102, approved design §10.4).
 *
 * The compact event page (`GET …/preview-runs/{ref}/events`) carries Brain's
 * `clinical-preview-trace.v1` events with their bodies BY REFERENCE, proxied
 * as JSON because this repository deliberately does not vendor Brain's
 * contract (design §8.3; the same decision `previewInsights.ts` records for
 * the envelope). This module reads those records into a shape the tab can
 * render and groups them into the timeline the design describes — turn,
 * phase, agent, then the decision cards in sequence order.
 *
 * Three rules, all inherited from devops-infrastructure ADR 0013:
 *
 *  - EVERY VALUE IS THE PRODUCER'S. Nothing here scores, ranks, compares a
 *    threshold, chooses a route or explains why one was not chosen. A
 *    `criterion_threshold` event says whether it passed; a `why_not` event
 *    says how the alternative stood; a `routing` event's status is fixed by
 *    Brain's own rule R28 and is rendered, never re-derived (N4, the
 *    ownership table);
 *  - ABSENCE IS A STATE, and the states are kept apart (N7): a criterion that
 *    was evaluated and not met, a classification the runtime set aside, an
 *    alternative whose standing the runtime could not determine, a body
 *    withheld by policy, a body withheld for size, and a range of events that
 *    was never captured are six different sentences here and six different
 *    renderings on the tab;
 *  - THE CLOSED VOCABULARIES STAY CLOSED. Reason codes, dispositions,
 *    operators and statuses are transcribed from the producer contract
 *    (`liv-brain:contracts/clinical-preview-trace.v1.schema.json`, rules R28
 *    and R19) and mapped to sentences; a code outside a list is shown AS a
 *    code this build does not know, never as the nearest known sentence.
 *
 * Pure and hermetic, like `previewInsights.ts`: the fast PR gate proves it
 * without a service, and every decision the component would otherwise make
 * inline — how to read a record, where it belongs in the timeline, what an
 * evidence reference resolves to — is a function here with a test beside it.
 */

// ── Closed vocabularies ──────────────────────────────────────────────────────

/** The nine event families, aliased from THIS service's contract (the allowlist its page read enforces). */
export type TraceEventKind = components["schemas"]["PreviewTraceEventKind"];

export const TRACE_EVENT_KINDS = [
  "turn",
  "prompt_call",
  "prompt_provenance",
  "classification",
  "criterion_threshold",
  "routing",
  "why_not",
  "safety",
  "runtime_transition",
] as const satisfies readonly TraceEventKind[];

export const EVENT_KIND_LABELS = {
  turn: "Conversation turn",
  prompt_call: "Prompt call",
  prompt_provenance: "Prompt provenance",
  classification: "Classification",
  criterion_threshold: "Criterion",
  routing: "Routing decision",
  why_not: "Alternative not selected",
  safety: "Safety outcome",
  runtime_transition: "Runtime transition",
} as const satisfies Record<TraceEventKind, string>;

/** The producer contract's closed event status (`eventCommon.status`). */
export const EVENT_STATUSES = [
  "ok",
  "no_action",
  "attention",
  "failed",
  "unavailable",
] as const;
export type TraceEventStatus = (typeof EVENT_STATUSES)[number];

/**
 * How a status reads on a card. `ok` is the unremarkable case and renders no
 * chip: the producer's R19 reserves the other four for outcomes a clinician
 * must not read as uneventful, so those are the ones that get a label.
 */
export const EVENT_STATUS_LABELS = {
  ok: "Recorded",
  no_action: "No action",
  attention: "Needs attention",
  failed: "Failed",
  unavailable: "Unavailable",
} as const satisfies Record<TraceEventStatus, string>;

export const EVENT_STATUS_TONES = {
  ok: "neutral",
  no_action: "outline",
  attention: "warning",
  failed: "destructive",
  unavailable: "destructive",
} as const satisfies Record<
  TraceEventStatus,
  "neutral" | "outline" | "warning" | "destructive"
>;

/** The producer contract's three visibility classes (design §6.2). */
export const VISIBILITIES = [
  "clinical_full",
  "protected_clinical",
  "restricted_technical",
] as const;
export type TraceVisibility = (typeof VISIBILITIES)[number];

export const VISIBILITY_LABELS = {
  clinical_full: "Clinical content",
  protected_clinical: "Protected — read-only",
  restricted_technical: "Restricted — technical internals withheld",
} as const satisfies Record<TraceVisibility, string>;

/** The three states of one clinical body (contract `$defs.content`, rule R11). */
export const CONTENT_STATES = ["inline", "reference", "omitted"] as const;
export type ContentState = (typeof CONTENT_STATES)[number];

export const CONTENT_ENCODINGS = ["text/plain", "application/json"] as const;
export type ContentEncoding = (typeof CONTENT_ENCODINGS)[number];

/** `routing.reasonCode` — closed by R28; the status each one fixes is on the event itself. */
export const ROUTING_REASON_CODES = [
  "highest_condition_count",
  "slot_pinned",
  "fallback_priority",
  "classification_unavailable",
  "pinned_slot_already_completed",
] as const;
export type RoutingReasonCode = (typeof ROUTING_REASON_CODES)[number];

export const ROUTING_REASON_LABELS = {
  highest_condition_count:
    "The weighted condition counts decided; a tie would be broken by the configured track priority.",
  slot_pinned:
    "A pinned selection slot answered outright; nothing was scored.",
  fallback_priority:
    "Scoring produced no count for any eligible choice, so the configured order decided.",
  classification_unavailable:
    "The classification these scores come from produced nothing, so the configured order decided without clinical evidence.",
  pinned_slot_already_completed:
    "The slot's pinned choice was already completed, so the configured order decided.",
} as const satisfies Record<RoutingReasonCode, string>;

/** `why_not.disposition` — the five standings the runtime itself distinguishes (R28). */
export const WHY_NOT_DISPOSITIONS = [
  "considered",
  "unconsidered",
  "already_completed",
  "ineligible",
  "unavailable",
] as const;
export type WhyNotDisposition = (typeof WHY_NOT_DISPOSITIONS)[number];

export const WHY_NOT_DISPOSITION_LABELS = {
  considered: "Weighed and not preferred",
  unconsidered: "Never weighed",
  already_completed: "Already completed in this conversation",
  ineligible: "Unselectable by a structural rule",
  unavailable: "Standing unknown — the runtime could not tell",
} as const satisfies Record<WhyNotDisposition, string>;

/** `why_not.reasonCode` — closed by R28 and bound to the disposition. */
export const WHY_NOT_REASON_CODES = [
  "lower_condition_count",
  "lower_track_priority",
  "tied_on_configured_priority",
  "lower_fallback_priority",
  "not_scored_by_classifier",
  "selection_pinned_to_slot",
  "track_already_completed",
  "reserved_for_later_slot",
  "classification_unavailable",
] as const;
export type WhyNotReasonCode = (typeof WHY_NOT_REASON_CODES)[number];

export const WHY_NOT_REASON_LABELS = {
  lower_condition_count:
    "It scored a lower weighted condition count than the selected route.",
  lower_track_priority:
    "It tied on score and the configured track priority ranked it lower.",
  tied_on_configured_priority:
    "It tied on score and on configured priority; nothing in the configuration separated them, so the selector kept the first it met.",
  lower_fallback_priority:
    "Nothing scored, and the configured fallback order ranked it lower.",
  not_scored_by_classifier:
    "The classifier produced no weight for it, so it was never scored.",
  selection_pinned_to_slot:
    "A pinned slot answered the selection outright, so it was never weighed.",
  track_already_completed: "This conversation had already run it.",
  reserved_for_later_slot: "It is reserved for a later pinned slot.",
  classification_unavailable:
    "The classification produced nothing, so its standing was never determined.",
} as const satisfies Record<WhyNotReasonCode, string>;

/**
 * `safety.outcome` — the seven states rule R27 keeps apart (#1103, design
 * §6.2 and §7.3). The producer binds each one to a reason-code set, a
 * detection state, an intervention shape and an event status, so a check that
 * FAILED, timed out, never ran or was configured off can never reach this page
 * wearing the state of one that ran and found nothing.
 */
export const SAFETY_OUTCOMES = [
  "not_run",
  "policy_disabled",
  "clear",
  "detected",
  "intervened",
  "failed",
  "unavailable",
] as const;
export type SafetyOutcomeState = (typeof SAFETY_OUTCOMES)[number];

/** The chip word. Seven distinct words; no two states share one. */
export const SAFETY_OUTCOME_LABELS = {
  not_run: "Not run",
  policy_disabled: "Configured off",
  clear: "Clear",
  detected: "Disclosure detected",
  intervened: "Intervened",
  failed: "Check failed",
  unavailable: "Check unavailable",
} as const satisfies Record<SafetyOutcomeState, string>;

/**
 * The chip tone. `clear` is the only state that reads as a clean result; the
 * two states where the check reached no verdict through a fault of its own
 * are `destructive`, and the three other unevaluated-or-detected states are
 * `warning` or `outline` — never `success`, and never the same tone as
 * `clear` (the DoD's "a failure cannot be mistaken for a clean result").
 */
export const SAFETY_OUTCOME_TONES = {
  not_run: "outline",
  policy_disabled: "warning",
  clear: "success",
  detected: "warning",
  intervened: "warning",
  failed: "destructive",
  unavailable: "destructive",
} as const satisfies Record<
  SafetyOutcomeState,
  "neutral" | "success" | "outline" | "warning" | "destructive"
>;

/**
 * What the STATE means, in one sentence each — about the check, never about
 * what the patient finally saw. What this mechanism did to the reply in front
 * of it is the producer's own `clinicalEffect`, which this page renders
 * verbatim and never paraphrases.
 */
export const SAFETY_OUTCOME_MEANINGS = {
  not_run: "This check did not run on this reply.",
  policy_disabled: "This check is configured off for this run, so it evaluated nothing.",
  clear: "This check ran and reached a verdict: no disclosure.",
  detected: "This check ran and found a disclosure.",
  intervened: "This check ran, found a disclosure and acted on the reply.",
  failed: "This check failed before reaching a verdict.",
  unavailable: "This check could not be run, so it reached no verdict.",
} as const satisfies Record<SafetyOutcomeState, string>;

/** `safety.reasonCode` — closed by R27 and bound to the outcome. */
export const SAFETY_REASON_CODES = [
  "empty_reply",
  "upstream_replaced",
  "mode_off",
  "no_signal",
  "evaluated_clean",
  "disclosure_detected",
  "evaluation_error",
  "runtime_error",
  "evaluation_timeout",
  "capacity_exhausted",
  "budget_exhausted",
] as const;
export type SafetyReasonCode = (typeof SAFETY_REASON_CODES)[number];

export const SAFETY_REASON_LABELS = {
  empty_reply: "The reply was empty, so there was nothing to check.",
  upstream_replaced: "An earlier safety mechanism had already replaced the reply.",
  mode_off: "The mechanism is configured off for this run.",
  no_signal: "The check found no disclosure signal.",
  evaluated_clean: "The check evaluated the reply and judged it discloses no internals.",
  disclosure_detected: "The check found a disclosure in the reply.",
  evaluation_error: "The check raised an error before reaching a verdict.",
  runtime_error: "The check failed unexpectedly before reaching a verdict.",
  evaluation_timeout: "The check did not finish within the turn budget.",
  capacity_exhausted: "No capacity was available to run the check.",
  budget_exhausted: "No turn budget remained to run the check.",
} as const satisfies Record<SafetyReasonCode, string>;

/**
 * `safety.detection` — whether the mechanism reached a verdict at all, and
 * what it was. THE ANSWER TO "was this reply checked?" IS THE PRODUCER'S,
 * read from this field; nothing here infers it from the outcome.
 */
export const SAFETY_DETECTIONS = ["not_evaluated", "not_triggered", "detected"] as const;
export type SafetyDetection = (typeof SAFETY_DETECTIONS)[number];

export const SAFETY_DETECTION_LABELS = {
  not_evaluated: "Not evaluated — this check reached no verdict on the reply.",
  not_triggered: "Evaluated — this check found no disclosure in the reply.",
  detected: "Evaluated — this check found a disclosure in the reply.",
} as const satisfies Record<SafetyDetection, string>;

/** `safety.intervention` — what the mechanism did to the reply it was given. */
export const SAFETY_INTERVENTIONS = ["none", "redirected", "blocked", "escalated"] as const;
export type SafetyIntervention = (typeof SAFETY_INTERVENTIONS)[number];

export const SAFETY_INTERVENTION_LABELS = {
  none: "None — this check did not change the reply.",
  redirected: "Redirected — this check replaced the reply with a safe redirect.",
  blocked: "Blocked — this check stopped the reply.",
  escalated: "Escalated — this check raised the reply for clinical attention.",
} as const satisfies Record<SafetyIntervention, string>;

/**
 * Rule R27 as a relation, not four independent vocabularies. A known outcome
 * is readable only with the reason, detection, intervention and presentation
 * status Brain binds to it; otherwise a contradictory document could put a
 * green `clear` claim over fields which say the check reached no verdict.
 */
const SAFETY_OUTCOME_RULES = {
  not_run: {
    reasons: ["empty_reply", "upstream_replaced"],
    detection: "not_evaluated",
    interventions: ["none"],
    status: "no_action",
  },
  policy_disabled: {
    reasons: ["mode_off"],
    detection: "not_evaluated",
    interventions: ["none"],
    status: "attention",
  },
  clear: {
    reasons: ["no_signal", "evaluated_clean"],
    detection: "not_triggered",
    interventions: ["none"],
    status: "ok",
  },
  detected: {
    reasons: ["disclosure_detected"],
    detection: "detected",
    interventions: ["none"],
    status: "attention",
  },
  intervened: {
    reasons: ["disclosure_detected"],
    detection: "detected",
    interventions: ["redirected", "blocked", "escalated"],
    status: "attention",
  },
  failed: {
    reasons: ["evaluation_error", "runtime_error"],
    detection: "not_evaluated",
    interventions: ["none"],
    status: "failed",
  },
  unavailable: {
    reasons: ["evaluation_timeout", "capacity_exhausted", "budget_exhausted"],
    detection: "not_evaluated",
    interventions: ["none"],
    status: "unavailable",
  },
} as const satisfies Record<
  SafetyOutcomeState,
  {
    reasons: readonly SafetyReasonCode[];
    detection: SafetyDetection;
    interventions: readonly SafetyIntervention[];
    status: TraceEventStatus;
  }
>;

/**
 * The safety mechanisms this build has a name for. OPEN at the contract
 * (`detail.mechanism` is short text), so an unnamed mechanism is shown by its
 * own identifier and never mapped onto one of these.
 */
export const SAFETY_MECHANISM_LABELS = {
  output_sanitizer: "Leak sanitizer",
  output_guard: "Output guard",
} as const;

/** `criterion_threshold.operator`. */
export const CRITERION_OPERATORS = ["gte", "gt", "lte", "lt", "eq", "ne"] as const;
export type CriterionOperator = (typeof CRITERION_OPERATORS)[number];

export const OPERATOR_LABELS = {
  gte: "at least",
  gt: "more than",
  lte: "at most",
  lt: "less than",
  eq: "equal to",
  ne: "not equal to",
} as const satisfies Record<CriterionOperator, string>;

/** `classification.rationaleState` (R14 binds it to `rationale`). */
export const RATIONALE_STATES = ["recorded", "not_recorded"] as const;
export type RationaleState = (typeof RATIONALE_STATES)[number];

export const RATIONALE_STATE_LABELS = {
  recorded: "An explicit rationale was recorded by the runtime.",
  not_recorded:
    "No explicit rationale was recorded. The structured facts above are what the runtime produced.",
} as const satisfies Record<RationaleState, string>;

/**
 * The producer's opaque event locator (`$defs.eventRef`). The ONLY shape this
 * page will ever put in an address or ask the service about: a value that
 * does not match it is not an event reference and is never requested.
 */
export const EVENT_REF_PATTERN = /^evt_[0-9a-f]{16,64}$/;

/**
 * The producer's content digest (`$defs.contentHash`). A digest is rendered
 * ON SCREEN as the identity of a body nobody may read, so it is held to its
 * shape rather than taken as a string: 64 hex characters cannot carry a
 * sentence, and a value that is not this is not a digest.
 */
export const CONTENT_HASH_PATTERN = /^sha256:[0-9a-f]{64}$/;

// ── Reading one record ───────────────────────────────────────────────────────

export type JsonRecord = { [key: string]: components["schemas"]["JsonValue"] };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/** An integral number in either spelling (the producer's R25), or null. */
function integerOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isInteger(value) ? value : null;
}

/** A byte count as the contract types it: a non-negative safe integer, or null. */
function byteSizeOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? value
    : null;
}

/** A content digest, or null for absent AND for anything that is not one. */
function digestOrNull(value: unknown): string | null {
  const text = stringOrNull(value);
  return text !== null && CONTENT_HASH_PATTERN.test(text) ? text : null;
}

function oneOf<T extends string>(
  list: readonly T[],
  value: unknown,
): value is T {
  return typeof value === "string" && (list as readonly string[]).includes(value);
}

function stringList(value: unknown): readonly string[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const items = value.filter((item): item is string => typeof item === "string");
  return items.length === value.length ? items : null;
}

/** A criterion's observed value or threshold: boolean, number or short text. */
export type CriterionScalar = boolean | number | string;

function scalarOrNull(value: unknown): CriterionScalar | null {
  return typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value)) ||
    typeof value === "string"
    ? value
    : null;
}

/**
 * One clinical body as the page carries it. A compact page carries
 * `reference` (fetch it through the event-detail read), a detail read carries
 * `inline`, and a withheld body is `omitted` with its reason and NO reference
 * to fetch (R8, R11) — which is what makes restricted content unreachable by
 * guessing rather than merely hidden.
 */
export type ContentSlot = {
  state: ContentState;
  inline: string | null;
  encoding: ContentEncoding | null;
  visibility: TraceVisibility | null;
  /**
   * The producer's digest of the body it held — the "version/digest" half of
   * a restricted summary. Null for absent AND for a value that is not a
   * `sha256:<64 hex>` digest: a slot renders this, so it is never carried as
   * free text.
   */
  contentHash: string | null;
  byteSize: number | null;
  omittedReason: OmissionReason | null;
  /**
   * True when a record classified `restricted_technical` did not arrive in
   * the one shape R8 permits — it carried a body or a reference, named
   * another state or reason, or described itself with metadata that is not
   * the contract's. See `readContentSlot`.
   */
  restrictedContentRefused: boolean;
};

/**
 * Reads one content slot.
 *
 * THE ONE RULE THAT IS NOT A TRANSCRIPTION: a slot the producer classifies
 * `restricted_technical` never leaves this function carrying a body, a
 * reference to one, or metadata that is not the contract's. The contract's R8
 * fixes restricted content as `omitted` at source with `inline` and
 * `contentRef` null and `omittedReason: restricted_technical` — Brain
 * withholds it, Tenant Liv validates the allowlisted response, and this
 * Editor's own event read refuses a page that carries an event outside the
 * allowlist — so a record that arrives here deviating from that shape has
 * already crossed three boundaries that forbid it, and the honest reading of
 * it is that the record is wrong, not that the clinician should see what it
 * carries.
 *
 * THE METADATA IS HELD TO ITS SHAPE TOO, and for the same reason the body is.
 * A restricted slot's whole product surface is a label, a digest and a size,
 * so the digest and the size are exactly the fields a defective or hostile
 * record would reach for: the Editor Service's body-free sweep looks for a
 * non-null `inline` and nothing else, so free text in `contentHash` crosses
 * it untouched. `contentHash` is therefore a `sha256:<64 hex>` value or it is
 * nothing — 64 hex characters cannot carry a sentence — and a declared digest
 * or size that is not the contract's REFUSES the slot rather than being
 * quietly dropped, because a card missing a digest reads like a producer that
 * did not record one.
 *
 * THIS IS NOT CLIENT-SIDE REDACTION, which the approved design forbids
 * (§6.2: "The frontend does not redact"). Redaction takes a body, removes the
 * parts that must not be shown and presents the rest as the content. Nothing
 * of the kind happens here or anywhere downstream: nothing is parsed,
 * searched, trimmed or shown — the slot is refused whole, and
 * `restrictedContentRefused` makes the refusal a state the card states out
 * loud rather than a silence that reads like an ordinary withheld body.
 */
export function readContentSlot(value: unknown): ContentSlot | null {
  if (!isRecord(value) || !oneOf(CONTENT_STATES, value["state"])) {
    return null;
  }
  const encoding = value["encoding"];
  const visibility = value["visibility"];
  const reason = value["omittedReason"];
  const slot: ContentSlot = {
    state: value["state"],
    inline: stringOrNull(value["inline"]),
    encoding: oneOf(CONTENT_ENCODINGS, encoding) ? encoding : null,
    visibility: oneOf(VISIBILITIES, visibility) ? visibility : null,
    contentHash: digestOrNull(value["contentHash"]),
    byteSize: byteSizeOrNull(value["byteSize"]),
    omittedReason: oneOf(OMISSION_REASONS, reason) ? reason : null,
    restrictedContentRefused: false,
  };
  if (slot.visibility !== "restricted_technical") {
    return slot;
  }
  // R8 fixes every restricted slot to one total shape. Although the base
  // content type permits null metadata for bodies that were never captured,
  // `restricted_technical` means the producer held and withheld a body: R10
  // therefore requires its digest and byte size. Check the raw null-only
  // fields too, so a missing property or a non-string body cannot be quietly
  // normalised into a compliant withholding.
  return {
    ...slot,
    state: "omitted",
    inline: null,
    restrictedContentRefused:
      slot.state !== "omitted" ||
      value["inline"] !== null ||
      value["contentRef"] !== null ||
      slot.encoding === null ||
      slot.omittedReason !== "restricted_technical" ||
      slot.contentHash === null ||
      slot.byteSize === null,
  };
}

/** Where in the runtime an event happened — each axis explicitly null where it does not apply. */
export type RuntimePosition = {
  agent: string | null;
  track: string | null;
  phase: string | null;
  step: string | null;
};

export type EventCoordinate = RuntimePosition & { turnNumber: number | null };

function readPosition(value: unknown): RuntimePosition | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    agent: stringOrNull(value["agent"]),
    track: stringOrNull(value["track"]),
    phase: stringOrNull(value["phase"]),
    step: stringOrNull(value["step"]),
  };
}

export type Score = { value: number; scale: string };

function readScore(value: unknown): Score | null {
  if (!isRecord(value)) {
    return null;
  }
  const scale = stringOrNull(value["scale"]);
  return typeof value["value"] === "number" &&
    Number.isFinite(value["value"]) &&
    scale !== null
    ? { value: value["value"], scale }
    : null;
}

export type ConfigIdentity = { id: string; version: string };

function readConfigIdentity(value: unknown): ConfigIdentity | null {
  if (!isRecord(value)) {
    return null;
  }
  const id = stringOrNull(value["id"]);
  const version = stringOrNull(value["version"]);
  return id !== null && version !== null ? { id, version } : null;
}

/** The fields every event carries (contract `eventCommon`). */
export type TraceEventCommon = {
  eventRef: string;
  sequence: number;
  occurredAt: string | null;
  kind: TraceEventKind;
  visibility: TraceVisibility;
  label: string;
  status: TraceEventStatus;
  coordinate: EventCoordinate;
};

export type TurnDetail = {
  userInput: ContentSlot | null;
  livResponse: ContentSlot | null;
  before: RuntimePosition;
  after: RuntimePosition;
};

export type ClassificationDetail = {
  classifierId: string;
  inputRefs: readonly string[];
  result: ContentSlot;
  rationaleState: RationaleState;
  rationale: ContentSlot | null;
  score: Score | null;
};

export type CriterionDetail = {
  criterionId: string;
  observedValue: CriterionScalar | null;
  /** The producer's operator, or the raw string when it is outside the closed list. */
  operator: CriterionOperator | { unknown: string };
  threshold: CriterionScalar | null;
  passed: boolean;
  sourceVersion: string;
};

export type RoutingDetail = {
  decisionId: string;
  consideredChoices: readonly string[];
  selectedChoice: string;
  /** Closed by R28; carried raw so a code this build does not know is SHOWN as unknown. */
  reasonCode: string;
  evidenceRefs: readonly string[];
  governingConfig: ConfigIdentity | null;
};

export type WhyNotDetail = {
  decisionEventRef: string;
  alternative: string;
  disposition: string;
  reasonCode: string;
  unmetCriteria: readonly string[];
  /** `null` — never `0` — when the selector held no number for it. */
  comparedScore: Score | null;
  evidenceRefs: readonly string[];
  governingConfig: ConfigIdentity | null;
};

export type TransitionDetail = {
  from: RuntimePosition;
  to: RuntimePosition;
  reasonCode: string | null;
};

/**
 * One safety mechanism's report on one reply surface (#1103).
 *
 * Every field is the producer's. The four closed vocabularies are carried RAW
 * — a code outside a list is shown AS a code this build does not know, never
 * as the nearest known state, and above all never as the clean one. The
 * mechanism is open short text at the contract, so it is carried as written.
 */
export type SafetyDetail = {
  mechanism: string;
  /** Closed by R27; raw so an unknown state is SHOWN as unknown. */
  outcome: string;
  reasonCode: string;
  detection: string;
  intervention: string;
  /**
   * What this mechanism did to the reply in front of it, in the producer's
   * own fixed words. Rendered verbatim and never paraphrased: it is the
   * protected clinical content of a safety event.
   */
  clinicalEffect: string;
  /** Required by the contract, so an event without a readable one is unreadable. */
  policyIdentity: ConfigIdentity;
  /**
   * The mechanism's internal configuration — restricted technical content,
   * withheld at source. Carries a digest and a size and never a body; see
   * `readContentSlot`. Required, like the policy identity.
   */
  guardInternals: ContentSlot;
};

export type TraceEvent = TraceEventCommon &
  (
    | { family: "turn"; detail: TurnDetail }
    | { family: "classification"; detail: ClassificationDetail }
    | { family: "criterion_threshold"; detail: CriterionDetail }
    | { family: "routing"; detail: RoutingDetail }
    | { family: "why_not"; detail: WhyNotDetail }
    | { family: "runtime_transition"; detail: TransitionDetail }
    | { family: "safety"; detail: SafetyDetail }
    /**
     * `prompt_call` and `prompt_provenance`: indexed so a decision's evidence
     * reference to one can be resolved and linked, never rendered as a card
     * here — the prompt families are the Prompt Inspector's.
     */
    | { family: "other" }
    /**
     * A family this tab renders whose `detail` is not the shape the producer
     * contract publishes for it. The event's own label, status and position
     * are still shown; the detail is stated as unreadable rather than
     * partially rendered.
     */
    | { family: "unreadable_detail"; reason: string }
  );

export type TraceEventOf<F extends TraceEvent["family"]> = Extract<
  TraceEvent,
  { family: F }
>;

export type UnreadableEventReading = {
  kind: "unreadable";
  reason:
    | "shape"
    | "eventRef"
    | "sequence"
    | "kind"
    | "visibility"
    | "label"
    | "status"
    | "coordinate";
  sequence: number | null;
  eventRef: string | null;
  /** The record's own `kind`, when it was a string, so an unknown family can be named. */
  eventKind: string | null;
};

export type TraceEventReading =
  | { kind: "event"; event: TraceEvent }
  | UnreadableEventReading;

/**
 * Reads one compact event record.
 *
 * Strict about the COMMON fields — a record without a well-formed reference, a
 * positive integer sequence, a closed kind, status and visibility, a label and
 * a coordinate is not an event this build can place, and is reported as
 * unreadable with the reason — and strict about each family's detail, which
 * either reads whole or is reported as an unreadable detail on an otherwise
 * placed event. Nothing is defaulted into shape.
 */
export function readTraceEvent(value: unknown): TraceEventReading {
  if (!isRecord(value)) {
    return { kind: "unreadable", reason: "shape", sequence: null, eventRef: null, eventKind: null };
  }
  const sequence = integerOrNull(value["sequence"]);
  const eventRef = stringOrNull(value["eventRef"]);
  const eventKind = stringOrNull(value["kind"]);
  const unreadable = (reason: UnreadableEventReading["reason"]): UnreadableEventReading => ({
    kind: "unreadable",
    reason,
    sequence: sequence !== null && sequence >= 1 ? sequence : null,
    eventRef: eventRef !== null && EVENT_REF_PATTERN.test(eventRef) ? eventRef : null,
    eventKind,
  });
  if (eventRef === null || !EVENT_REF_PATTERN.test(eventRef)) {
    return unreadable("eventRef");
  }
  if (sequence === null || sequence < 1) {
    return unreadable("sequence");
  }
  if (!oneOf(TRACE_EVENT_KINDS, eventKind)) {
    return unreadable("kind");
  }
  const visibility = value["visibility"];
  if (!oneOf(VISIBILITIES, visibility)) {
    return unreadable("visibility");
  }
  const label = stringOrNull(value["label"]);
  if (label === null || label.trim().length === 0) {
    return unreadable("label");
  }
  const status = value["status"];
  if (!oneOf(EVENT_STATUSES, status)) {
    return unreadable("status");
  }
  const position = readPosition(value["coordinate"]);
  if (position === null) {
    return unreadable("coordinate");
  }
  const coordinate: EventCoordinate = {
    ...position,
    turnNumber: integerOrNull((value["coordinate"] as Record<string, unknown>)["turnNumber"]),
  };
  const common: TraceEventCommon = {
    eventRef,
    sequence,
    occurredAt: stringOrNull(value["occurredAt"]),
    kind: eventKind,
    visibility,
    label,
    status,
    coordinate,
  };
  return { kind: "event", event: readDetail(common, value["detail"]) };
}

function readDetail(common: TraceEventCommon, detail: unknown): TraceEvent {
  const unreadable = (reason: string): TraceEvent => ({
    ...common,
    family: "unreadable_detail",
    reason,
  });
  switch (common.kind) {
    case "prompt_call":
    case "prompt_provenance":
      return { ...common, family: "other" };
    case "safety": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const mechanism = stringOrNull(detail["mechanism"]);
      const outcome = stringOrNull(detail["outcome"]);
      const reasonCode = stringOrNull(detail["reasonCode"]);
      const detection = stringOrNull(detail["detection"]);
      const intervention = stringOrNull(detail["intervention"]);
      const clinicalEffect = stringOrNull(detail["clinicalEffect"]);
      // Each field is named separately: a safety card that silently lost one
      // of these would read as a state the producer never reported.
      if (mechanism === null) {
        return unreadable("mechanism");
      }
      if (outcome === null) {
        return unreadable("outcome");
      }
      if (reasonCode === null) {
        return unreadable("reasonCode");
      }
      if (detection === null) {
        return unreadable("detection");
      }
      if (intervention === null) {
        return unreadable("intervention");
      }
      if (clinicalEffect === null) {
        return unreadable("clinicalEffect");
      }
      // R27 is one atomic clinical claim. Preserve forward compatibility for
      // a future outcome by rendering it as unknown, but never render a KNOWN
      // outcome unless every field and the event's presentation status agree
      // with the producer's relation. In particular, `clear` must not acquire
      // a success treatment over a reason or detection that says no verdict.
      if (oneOf(SAFETY_OUTCOMES, outcome)) {
        const rule = SAFETY_OUTCOME_RULES[outcome];
        if (common.status !== rule.status) {
          return unreadable("status");
        }
        if (!(rule.reasons as readonly string[]).includes(reasonCode)) {
          return unreadable("reasonCode");
        }
        if (detection !== rule.detection) {
          return unreadable("detection");
        }
        if (!(rule.interventions as readonly string[]).includes(intervention)) {
          return unreadable("intervention");
        }
      }
      // REQUIRED, both of them (`detail_safety.required`), and both of them
      // are what this leaf exists to show: the protected floor's identity and
      // the fact that the internals were withheld. Read as absences they would
      // render "Not reported" and "no technical-internals record at all" on an
      // otherwise ordinary card — a malformed `clear` event presented as a
      // clean one, with the drift that produced it invisible.
      const policyIdentity = readConfigIdentity(detail["policyIdentity"]);
      if (policyIdentity === null) {
        return unreadable("policyIdentity");
      }
      const guardInternals = readContentSlot(detail["guardInternals"]);
      // The family fixes this slot's visibility independently of what an
      // incoming record calls itself. Without this check a clinical/missing
      // visibility bypasses `readContentSlot`'s restricted branch and turns a
      // malformed guard record into an ordinary withholding summary.
      if (guardInternals === null || guardInternals.visibility !== "restricted_technical") {
        return unreadable("guardInternals");
      }
      return {
        ...common,
        family: "safety",
        detail: {
          mechanism,
          outcome,
          reasonCode,
          detection,
          intervention,
          clinicalEffect,
          policyIdentity,
          guardInternals,
        },
      };
    }
    case "turn": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const before = readPosition(detail["before"]);
      const after = readPosition(detail["after"]);
      if (before === null || after === null) {
        return unreadable("position");
      }
      return {
        ...common,
        family: "turn",
        detail: {
          userInput: readContentSlot(detail["userInput"]),
          livResponse: readContentSlot(detail["livResponse"]),
          before,
          after,
        },
      };
    }
    case "classification": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const classifierId = stringOrNull(detail["classifierId"]);
      const inputRefs = stringList(detail["inputRefs"]);
      const result = readContentSlot(detail["result"]);
      const rationaleState = detail["rationaleState"];
      if (classifierId === null) {
        return unreadable("classifierId");
      }
      if (inputRefs === null) {
        return unreadable("inputRefs");
      }
      if (result === null) {
        return unreadable("result");
      }
      if (!oneOf(RATIONALE_STATES, rationaleState)) {
        return unreadable("rationaleState");
      }
      return {
        ...common,
        family: "classification",
        detail: {
          classifierId,
          inputRefs,
          result,
          rationaleState,
          rationale: readContentSlot(detail["rationale"]),
          score: readScore(detail["score"]),
        },
      };
    }
    case "criterion_threshold": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const criterionId = stringOrNull(detail["criterionId"]);
      const sourceVersion = stringOrNull(detail["sourceVersion"]);
      const operator = detail["operator"];
      if (criterionId === null) {
        return unreadable("criterionId");
      }
      if (typeof detail["passed"] !== "boolean") {
        return unreadable("passed");
      }
      if (sourceVersion === null) {
        return unreadable("sourceVersion");
      }
      return {
        ...common,
        family: "criterion_threshold",
        detail: {
          criterionId,
          observedValue: scalarOrNull(detail["observedValue"]),
          operator: oneOf(CRITERION_OPERATORS, operator)
            ? operator
            : { unknown: typeof operator === "string" ? operator : "" },
          threshold: scalarOrNull(detail["threshold"]),
          passed: detail["passed"],
          sourceVersion,
        },
      };
    }
    case "routing": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const decisionId = stringOrNull(detail["decisionId"]);
      const consideredChoices = stringList(detail["consideredChoices"]);
      const selectedChoice = stringOrNull(detail["selectedChoice"]);
      const reasonCode = stringOrNull(detail["reasonCode"]);
      const evidenceRefs = stringList(detail["evidenceRefs"]);
      if (decisionId === null) {
        return unreadable("decisionId");
      }
      if (consideredChoices === null) {
        return unreadable("consideredChoices");
      }
      if (selectedChoice === null) {
        return unreadable("selectedChoice");
      }
      if (reasonCode === null) {
        return unreadable("reasonCode");
      }
      if (evidenceRefs === null) {
        return unreadable("evidenceRefs");
      }
      return {
        ...common,
        family: "routing",
        detail: {
          decisionId,
          consideredChoices,
          selectedChoice,
          reasonCode,
          evidenceRefs,
          governingConfig: readConfigIdentity(detail["governingConfig"]),
        },
      };
    }
    case "why_not": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const decisionEventRef = stringOrNull(detail["decisionEventRef"]);
      const alternative = stringOrNull(detail["alternative"]);
      const disposition = stringOrNull(detail["disposition"]);
      const reasonCode = stringOrNull(detail["reasonCode"]);
      const unmetCriteria = stringList(detail["unmetCriteria"]);
      const evidenceRefs = stringList(detail["evidenceRefs"]);
      if (decisionEventRef === null) {
        return unreadable("decisionEventRef");
      }
      if (alternative === null) {
        return unreadable("alternative");
      }
      if (disposition === null) {
        return unreadable("disposition");
      }
      if (reasonCode === null) {
        return unreadable("reasonCode");
      }
      if (unmetCriteria === null) {
        return unreadable("unmetCriteria");
      }
      if (evidenceRefs === null) {
        return unreadable("evidenceRefs");
      }
      return {
        ...common,
        family: "why_not",
        detail: {
          decisionEventRef,
          alternative,
          disposition,
          reasonCode,
          unmetCriteria,
          comparedScore: readScore(detail["comparedScore"]),
          evidenceRefs,
          governingConfig: readConfigIdentity(detail["governingConfig"]),
        },
      };
    }
    case "runtime_transition": {
      if (!isRecord(detail)) {
        return unreadable("detail");
      }
      const from = readPosition(detail["from"]);
      const to = readPosition(detail["to"]);
      if (from === null || to === null) {
        return unreadable("position");
      }
      return {
        ...common,
        family: "runtime_transition",
        detail: { from, to, reasonCode: stringOrNull(detail["reasonCode"]) },
      };
    }
  }
}

// ── The timeline ─────────────────────────────────────────────────────────────

export type ClassificationEvent = TraceEventOf<"classification">;
export type CriterionEvent = TraceEventOf<"criterion_threshold">;
export type RoutingEvent = TraceEventOf<"routing">;
export type WhyNotEvent = TraceEventOf<"why_not">;
export type TransitionEvent = TraceEventOf<"runtime_transition">;
export type SafetyEvent = TraceEventOf<"safety">;
export type TurnEvent = TraceEventOf<"turn">;

/**
 * One entry of an aggregated card's transcript — the criteria beneath a
 * classification, the alternatives beneath a routing decision — in sequence
 * order: an event of the card's own family, or a declared missing range that
 * interrupted the transcript at that sequence (#1122 review).
 */
export type TranscriptEntry<E extends TraceEvent> =
  | { kind: "event"; event: E }
  | { kind: "gap"; range: MissingRange; sequence: number };

export function transcriptSequence(entry: TranscriptEntry<TraceEvent>): number {
  return entry.kind === "event" ? entry.event.sequence : entry.sequence;
}

export type TimelineItem =
  /** A classification with the criteria the engine evaluated from it, in the exact order it did. */
  | {
      kind: "classification";
      event: ClassificationEvent;
      criteria: readonly CriterionEvent[];
      /** The `<classifierId>.diagnosis` verdict criterion, when the transcript holds one. */
      verdict: CriterionEvent | null;
      /** The criteria in sequence order, with any declared gap that fell between them. */
      transcript: readonly TranscriptEntry<CriterionEvent>[];
    }
  /** A criterion no classification in its agent group owns — a fan-out parent's verdict, for instance. */
  | { kind: "criterion"; event: CriterionEvent }
  /** A routing decision with every alternative that names it, in the producer's order. */
  | {
      kind: "routing";
      event: RoutingEvent;
      alternatives: readonly WhyNotEvent[];
      /** The alternatives in sequence order, with any declared gap that fell between them. */
      transcript: readonly TranscriptEntry<WhyNotEvent>[];
    }
  /** An alternative whose decision is not among the loaded events. */
  | { kind: "why_not"; event: WhyNotEvent }
  | { kind: "transition"; event: TransitionEvent }
  /** One safety mechanism's report on one reply surface, placed at its own sequence. */
  | { kind: "safety"; event: SafetyEvent }
  | { kind: "unreadable_detail"; event: TraceEventOf<"unreadable_detail"> }
  | { kind: "unreadable"; reading: UnreadableEventReading }
  /**
   * A declared missing range, placed at its own sequence among the cards it
   * interrupted: after the last card that precedes it, in that card's group.
   */
  | { kind: "gap"; range: MissingRange; sequence: number };

/** The sequence a card is placed by: its head event's, or a gap's start. */
export function itemSequence(item: TimelineItem): number | null {
  switch (item.kind) {
    case "gap":
      return item.sequence;
    case "unreadable":
      return item.reading.sequence;
    default:
      return item.event.sequence;
  }
}

export type TimelineAgent = { agent: string | null; items: readonly TimelineItem[] };
export type TimelinePhase = { phase: string | null; agents: readonly TimelineAgent[] };

export type TimelineTurn = {
  /** The turn coordinate the group's events carry; null for events that carry none before any turn. */
  turnNumber: number | null;
  /** The turn's own `turn` event, when the trace holds it. */
  turn: TurnEvent | null;
  phases: readonly TimelinePhase[];
  /**
   * Declared missing ranges inside this turn that precede its first card —
   * typically a range lost right after the turn event. A range that follows a
   * card is a `gap` item in that card's group, at its own sequence.
   */
  leadingGaps: readonly MissingRange[];
  firstSequence: number;
};

export type DecisionTimeline = {
  turns: readonly TimelineTurn[];
  /** Declared missing ranges that precede every loaded event. */
  leadingGaps: readonly MissingRange[];
  /** Readings this build could not even place — no usable sequence. */
  unplaceable: readonly UnreadableEventReading[];
  /** Events of the decision families this tab renders. */
  decisionCount: number;
  /** Safety events this tab renders — counted apart, because they are not decisions. */
  safetyCount: number;
  /** Events of the families other tabs render (the two prompt families). */
  otherCount: number;
  /** Every readable event by its reference — the resolver's universe. */
  index: ReadonlyMap<string, TraceEvent>;
};

function sequenceOf(reading: TraceEventReading): number | null {
  return reading.kind === "event" ? reading.event.sequence : reading.sequence;
}

function refOf(reading: TraceEventReading): string {
  return (reading.kind === "event" ? reading.event.eventRef : reading.eventRef) ?? "";
}

/**
 * A total order over readings: by sequence, then by reference. The producer's
 * R4 makes sequences unique, so the second key only ever settles a defective
 * page — but it is what makes two builds of one page byte-identical whatever
 * order the records arrived in.
 */
function compareReadings(a: TraceEventReading, b: TraceEventReading): number {
  const left = sequenceOf(a) ?? Number.POSITIVE_INFINITY;
  const right = sequenceOf(b) ?? Number.POSITIVE_INFINITY;
  if (left !== right) {
    return left - right;
  }
  return refOf(a) < refOf(b) ? -1 : refOf(a) > refOf(b) ? 1 : 0;
}

const DECISION_FAMILIES = new Set<TraceEvent["family"]>([
  "classification",
  "criterion_threshold",
  "routing",
  "why_not",
  "runtime_transition",
]);

type MutableAgent = { agent: string | null; items: TimelineItem[] };
type MutablePhase = { phase: string | null; agents: MutableAgent[] };
type MutableTurn = {
  turnNumber: number | null;
  turn: TurnEvent | null;
  phases: MutablePhase[];
  leadingGaps: MissingRange[];
  firstSequence: number;
};

/**
 * Groups the readable events into the timeline the design describes
 * (§10.4): by turn, then phase, then agent, with the cards inside each group
 * in sequence order.
 *
 * DETERMINISTIC AND ORDER-INDEPENDENT. The records are sorted by sequence
 * first, so two pages holding the same events in any order build one
 * timeline; every group appears where its FIRST event does, so groups never
 * reorder as later events arrive.
 *
 * ATTACHMENT IS BY THE PRODUCER'S OWN IDENTITIES, never by adjacency alone:
 *
 *  - a criterion belongs to the classification in its agent group whose
 *    `classifierId` is the longest prefix of its `criterionId`
 *    (`depression_screening.yes_count` → `depression_screening`), which is the
 *    identity the producer documents for exactly this cross-reference. A
 *    criterion owned by no loaded classification — a fan-out parent's verdict
 *    has no classification event of its own — stands alone;
 *  - an alternative belongs to the routing decision its `decisionEventRef`
 *    names, wherever that decision sits. One whose decision is not loaded is
 *    an item in its own right, stated as such, rather than attached to the
 *    nearest decision — a wrong attribution is worse than a missing one.
 *
 * An event carrying no turn coordinate is placed in the turn in effect at its
 * sequence (the producer's R5: such events do not reset the turn), or before
 * the first turn when none precedes it. Declared missing ranges are placed
 * the same way, by the sequence they start at.
 */
export function buildDecisionTimeline(
  records: readonly unknown[],
  completeness: Pick<TraceCompleteness, "missingRanges"> | null,
): DecisionTimeline {
  const readings = records.map(readTraceEvent).sort(compareReadings);
  const index = new Map<string, TraceEvent>();
  for (const reading of readings) {
    if (reading.kind === "event" && !index.has(reading.event.eventRef)) {
      index.set(reading.event.eventRef, reading.event);
    }
  }

  const turns: MutableTurn[] = [];
  const unplaceable: UnreadableEventReading[] = [];
  const routingItems = new Map<
    string,
    { event: RoutingEvent; alternatives: WhyNotEvent[]; transcript: TranscriptEntry<WhyNotEvent>[] }
  >();
  const orphanAlternatives: { reading: WhyNotEvent; agent: MutableAgent }[] = [];
  let decisionCount = 0;
  let safetyCount = 0;
  let otherCount = 0;

  const turnFor = (turnNumber: number | null, sequence: number): MutableTurn => {
    if (turnNumber !== null) {
      const existing = turns.find((turn) => turn.turnNumber === turnNumber);
      if (existing) {
        return existing;
      }
      const created: MutableTurn = { turnNumber, turn: null, phases: [], leadingGaps: [], firstSequence: sequence };
      turns.push(created);
      return created;
    }
    const inEffect = turns.at(-1);
    if (inEffect) {
      return inEffect;
    }
    const leading: MutableTurn = { turnNumber: null, turn: null, phases: [], leadingGaps: [], firstSequence: sequence };
    turns.push(leading);
    return leading;
  };

  const agentFor = (turn: MutableTurn, position: RuntimePosition): MutableAgent => {
    let phase = turn.phases.find((candidate) => candidate.phase === position.phase);
    if (!phase) {
      phase = { phase: position.phase, agents: [] };
      turn.phases.push(phase);
    }
    let agent = phase.agents.find((candidate) => candidate.agent === position.agent);
    if (!agent) {
      agent = { agent: position.agent, items: [] };
      phase.agents.push(agent);
    }
    return agent;
  };

  for (const reading of readings) {
    if (reading.kind === "unreadable") {
      if (reading.sequence === null) {
        unplaceable.push(reading);
        continue;
      }
      const turn = turnFor(null, reading.sequence);
      agentFor(turn, { agent: null, track: null, phase: null, step: null }).items.push({
        kind: "unreadable",
        reading,
      });
      continue;
    }
    const event = reading.event;
    if (event.family === "other") {
      otherCount += 1;
      // The turn a prompt event carries still opens the group, so a turn made
      // only of prompt calls is present (and empty) rather than absent.
      turnFor(event.coordinate.turnNumber, event.sequence);
      continue;
    }
    const turn = turnFor(event.coordinate.turnNumber, event.sequence);
    if (event.family === "turn") {
      if (turn.turn === null) {
        turn.turn = event;
      }
      continue;
    }
    if (DECISION_FAMILIES.has(event.family)) {
      decisionCount += 1;
    }
    if (event.family === "safety") {
      safetyCount += 1;
    }
    const agent = agentFor(turn, event.coordinate);
    switch (event.family) {
      case "classification":
        agent.items.push({
          kind: "classification",
          event,
          criteria: [],
          verdict: null,
          transcript: [],
        });
        break;
      case "criterion_threshold": {
        const owner = owningClassification(agent.items, event.detail.criterionId);
        if (owner) {
          (owner.criteria as CriterionEvent[]).push(event);
          (owner.transcript as TranscriptEntry<CriterionEvent>[]).push({ kind: "event", event });
          if (event.detail.criterionId === `${owner.event.detail.classifierId}.diagnosis`) {
            owner.verdict = event;
          }
        } else {
          agent.items.push({ kind: "criterion", event });
        }
        break;
      }
      case "routing": {
        const item = {
          kind: "routing" as const,
          event,
          alternatives: [] as WhyNotEvent[],
          transcript: [] as TranscriptEntry<WhyNotEvent>[],
        };
        agent.items.push(item);
        routingItems.set(event.eventRef, item);
        break;
      }
      case "why_not": {
        const decision = routingItems.get(event.detail.decisionEventRef);
        if (decision) {
          decision.alternatives.push(event);
        } else {
          orphanAlternatives.push({ reading: event, agent });
        }
        break;
      }
      case "runtime_transition":
        agent.items.push({ kind: "transition", event });
        break;
      case "safety":
        agent.items.push({ kind: "safety", event });
        break;
      case "unreadable_detail":
        agent.items.push({ kind: "unreadable_detail", event });
        break;
    }
  }

  // An alternative whose decision was numbered AFTER it (a defective page) or
  // never loaded: the producer's R9 forbids the first, but a page is not the
  // producer, so it is placed rather than dropped.
  for (const orphan of orphanAlternatives) {
    const decision = routingItems.get(orphan.reading.detail.decisionEventRef);
    if (decision) {
      decision.alternatives.push(orphan.reading);
    } else {
      orphan.agent.items.push({ kind: "why_not", event: orphan.reading });
    }
  }
  for (const item of routingItems.values()) {
    item.alternatives.sort((a, b) => a.sequence - b.sequence);
    item.transcript.splice(
      0,
      item.transcript.length,
      ...item.alternatives.map((event): TranscriptEntry<WhyNotEvent> => ({ kind: "event", event })),
    );
  }

  const leadingGaps: MissingRange[] = [];
  const ranges = [...(completeness?.missingRanges ?? [])].sort(
    (a, b) => (a.fromSequence ?? 0) - (b.fromSequence ?? 0),
  );
  for (const range of ranges) {
    const from = range.fromSequence;
    const owner =
      from === null
        ? null
        : [...turns].reverse().find((turn) => turn.firstSequence <= from);
    if (owner === undefined || owner === null || from === null) {
      leadingGaps.push(range);
    } else {
      placeGap(owner, range, from);
    }
  }

  return {
    turns,
    leadingGaps,
    unplaceable,
    decisionCount,
    safetyCount,
    otherCount,
    index,
  };
}

type AggregatedItem = Extract<TimelineItem, { kind: "classification" | "routing" }>;

/** Where a declared range lands: the element with the greatest sequence before it. */
type GapSlot = {
  agent: MutableAgent;
  /** The card's index in its agent group. */
  index: number;
  /** The aggregated card the element belongs to, when it does; null for a plain card. */
  card: AggregatedItem | null;
  /** The element's index inside the card's transcript, or -1 for the card's own head. */
  entryIndex: number;
  sequence: number;
};

/**
 * Places a declared missing range at its own position in the turn's stream
 * (#1122 review, both rounds). The element with the greatest sequence before
 * the range decides:
 *
 *  - when that element is inside an aggregated card — a criterion beneath a
 *    classification, an alternative beneath a routing decision, or the card's
 *    own head — and the card's transcript CONTINUES past the range, the gap
 *    is an entry of that transcript, right after the element (or first, when
 *    the head is what precedes it): the card renders the criteria it holds
 *    in sequence order, so a range lost between two of them is shown between
 *    them, never after the card that still shows a later one;
 *  - otherwise the gap is a card of its own, right after the preceding card
 *    in that card's agent group;
 *  - when nothing in the turn precedes it, it leads the turn — where a range
 *    lost right after the turn event lands.
 *
 * Cards are grouped by phase and agent, so "after the element that precedes
 * it" is the order a reader meets. Ranges are placed in ascending order, and a
 * placed gap counts as an element for the next one, so two gaps behind one
 * element keep their order.
 */
function placeGap(turn: MutableTurn, range: MissingRange, from: number): void {
  let best: GapSlot | null = null;
  const consider = (slot: GapSlot) => {
    if (slot.sequence < from && (best === null || slot.sequence >= best.sequence)) {
      best = slot;
    }
  };
  for (const phase of turn.phases) {
    for (const agent of phase.agents) {
      for (let index = 0; index < agent.items.length; index += 1) {
        const item = agent.items[index];
        if (item === undefined) {
          continue;
        }
        const aggregated = item.kind === "classification" || item.kind === "routing" ? item : null;
        const head = itemSequence(item);
        if (head !== null) {
          consider({ agent, index, card: aggregated, entryIndex: -1, sequence: head });
        }
        if (aggregated !== null) {
          for (let entryIndex = 0; entryIndex < aggregated.transcript.length; entryIndex += 1) {
            const entry = aggregated.transcript[entryIndex];
            if (entry !== undefined) {
              consider({
                agent,
                index,
                card: aggregated,
                entryIndex,
                sequence: transcriptSequence(entry),
              });
            }
          }
        }
      }
    }
  }
  if (best === null) {
    turn.leadingGaps.push(range);
    return;
  }
  const slot: GapSlot = best;
  const gap = { kind: "gap" as const, range, sequence: from };
  const continues =
    slot.card !== null &&
    slot.card.transcript.some((entry) => transcriptSequence(entry) > from);
  if (slot.card !== null && continues) {
    (slot.card.transcript as TranscriptEntry<TraceEvent>[]).splice(slot.entryIndex + 1, 0, gap);
    return;
  }
  slot.agent.items.splice(slot.index + 1, 0, gap);
}

function owningClassification(
  items: readonly TimelineItem[],
  criterionId: string,
): Extract<TimelineItem, { kind: "classification" }> | null {
  let best: Extract<TimelineItem, { kind: "classification" }> | null = null;
  for (const item of items) {
    if (item.kind !== "classification") {
      continue;
    }
    const prefix = `${item.event.detail.classifierId}.`;
    if (
      criterionId.startsWith(prefix) &&
      (best === null || prefix.length >= `${best.event.detail.classifierId}.`.length)
    ) {
      best = item;
    }
  }
  return best;
}

// ── Evidence references ──────────────────────────────────────────────────────

/**
 * The run-page tab a resolved reference opens.
 *
 * Every family this page can list now has a tab that renders it, so there is
 * no "nowhere to go" member: #1102 gave `safety` one because its presentation
 * was a later leaf's, and #1103 is that leaf. A tenth family would need a tab
 * decided for it rather than a silent fall-through.
 */
export type EvidenceDestination = RunTabId;

export const EVIDENCE_DESTINATIONS = {
  turn: "conversation",
  prompt_call: "prompts",
  prompt_provenance: "prompts",
  classification: "decisions",
  criterion_threshold: "decisions",
  routing: "decisions",
  why_not: "decisions",
  runtime_transition: "decisions",
  safety: "decisions",
} as const satisfies Record<TraceEventKind, EvidenceDestination>;

export type EvidenceResolution =
  | { kind: "resolved"; event: TraceEvent; destination: EvidenceDestination }
  /**
   * The citing event declared this reference lost (the producer's R9: a
   * reference that cannot resolve is declared as a capture omission on the
   * event that made it). Rendered as "not captured", never as absent.
   */
  | { kind: "declared_lost"; reason: OmissionReason | null }
  /** Not among the loaded events and not declared — stated, never guessed at. */
  | { kind: "unresolved" };

/**
 * Resolves one reference a decision made, against the loaded events ONLY. A
 * reference is never requested from the service to find out what it is: the
 * universe of things this page can link to is exactly what the run's own
 * event page listed, which is what "cannot request arbitrary event refs"
 * means in practice.
 */
export function resolveEvidence(
  ref: string,
  citing: {
    eventRef: string;
    field: string;
    /** The reference's position in a list-valued field, for an `inputRefs[2]`-shaped declaration. */
    index?: number;
  },
  index: ReadonlyMap<string, TraceEvent>,
  completeness: Pick<TraceCompleteness, "omissions"> | null,
): EvidenceResolution {
  const event = index.get(ref);
  if (event) {
    return { kind: "resolved", event, destination: EVIDENCE_DESTINATIONS[event.kind] };
  }
  // The declaration names THIS reference: the whole field, or the field at
  // this reference's own position. A neighbour's loss does not explain it.
  const declared = completeness?.omissions.find(
    (omission) =>
      omission.eventRef === citing.eventRef &&
      omission.field !== null &&
      (omission.field === citing.field ||
        (citing.index !== undefined && omission.field === `${citing.field}[${citing.index}]`)),
  );
  if (declared) {
    return { kind: "declared_lost", reason: declared.reason };
  }
  return { kind: "unresolved" };
}

// ── The run page's tabs and their addresses ──────────────────────────────────

export const RUN_TABS = [
  "conversation",
  "prompts",
  "decisions",
  "compare",
] as const;
export type RunTabId = (typeof RUN_TABS)[number];

export const RUN_TAB_LABELS = {
  conversation: "Conversation",
  prompts: "Prompt Inspector",
  decisions: "Flow & Decisions",
  compare: "Compare",
} as const satisfies Record<RunTabId, string>;

export const RUN_TAB_PARAM = "tab";
export const RUN_EVENT_PARAM = "event";

/** The tab an address names, or null for none or one this page does not have. */
export function readRunTabParam(value: string | null): RunTabId | null {
  return oneOf(RUN_TABS, value) ? value : null;
}

/**
 * The event an address names — but ONLY when it is shaped like a producer
 * reference. Anything else is not an event reference and is dropped before it
 * can reach a query key, a request or the screen.
 */
export function readEventRefParam(value: string | null): string | null {
  return value !== null && EVENT_REF_PATTERN.test(value) ? value : null;
}

/**
 * The run page's address with a tab, and optionally the event that tab should
 * open on. Built ON `previewRunPath`, so an evidence link carries exactly what
 * the run page's own address carries — the Project id and the opaque locator —
 * plus a tab name and an opaque event reference the run's own event page
 * listed. Nothing the run is ABOUT (candidate, patient, target, Liv or Brain
 * session) can enter a link, because nothing here takes one (ADR 0013 N6).
 */
export function previewRunTabPath(
  projectId: string,
  previewRunRef: string,
  tab: RunTabId,
  eventRef?: string,
): string {
  const params = new URLSearchParams();
  params.set(RUN_TAB_PARAM, tab);
  if (eventRef !== undefined && EVENT_REF_PATTERN.test(eventRef)) {
    params.set(RUN_EVENT_PARAM, eventRef);
  }
  return `${previewRunPath(projectId, previewRunRef)}?${params.toString()}`;
}

// ── Sentences ────────────────────────────────────────────────────────────────

/** A criterion's own verdict, in the engine's words: it evaluated, and this is what it found. */
export function describeCriterionResult(passed: boolean): "Met" | "Not met" {
  return passed ? "Met" : "Not met";
}

/** A boolean, number or short text as a clinician reads it; null as "not reported". */
export function formatScalar(value: CriterionScalar | null): string {
  if (value === null) {
    return "not reported";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}

export function describeOperator(operator: CriterionDetail["operator"]): string {
  return typeof operator === "string"
    ? OPERATOR_LABELS[operator]
    : `compared with an operator this build does not know (${operator.unknown || "unnamed"})`;
}

/**
 * Why a body is not readable here, or null when it is. The five reasons are
 * kept apart because only one of them is an ordinary policy decision:
 * restricted content was WITHHELD on purpose and leaves the trace complete,
 * two are shortfalls, and the last is a record this build refused (design
 * §7.4; ADR 0013 N7).
 */
export function describeWithheld(slot: ContentSlot): string | null {
  if (slot.restrictedContentRefused) {
    return (
      "Withheld by policy: restricted technical content. This record also carried " +
      "content for it, which this build did not read and did not show — the producer " +
      "is required to withhold it at source, so report this run to an operator."
    );
  }
  if (slot.state !== "omitted") {
    return null;
  }
  switch (slot.omittedReason) {
    case "restricted_technical":
      return "Withheld by policy: restricted technical content. Not a gap in the capture.";
    case "size_cap_exceeded":
      return "Withheld whole: the body crossed the size cap. The trace is incomplete here.";
    case "capture_unavailable":
      return "Not captured: capture failed or never ran for this body.";
    case "unsupported_producer_version":
      return "Not readable: the serving Brain predates this trace version.";
    case null:
      // A RESTRICTED slot cannot reach this: `readContentSlot` refuses one
      // whose reason is not `restricted_technical`, and the refusal is
      // answered above. This is the clinical-content case only.
      return "Withheld, for a reason this build does not know.";
  }
}

/**
 * One safety state as this build reads it: the producer's own code when it is
 * one this build knows, and the code itself when it is not. An unknown state
 * is never resolved to the nearest known one, and never to the clean one.
 */
export function readSafetyOutcome(code: string): SafetyOutcomeState | null {
  return oneOf(SAFETY_OUTCOMES, code) ? code : null;
}

export function describeSafetyOutcome(code: string): string {
  const known = readSafetyOutcome(code);
  return known !== null
    ? SAFETY_OUTCOME_MEANINGS[known]
    : "This build does not know this safety state, so it cannot say what the check did. Do not read it as a clean result.";
}

export function safetyOutcomeLabel(code: string): string {
  const known = readSafetyOutcome(code);
  return known !== null ? SAFETY_OUTCOME_LABELS[known] : "Outcome not known to this build";
}

/**
 * The chip tone for a safety state. An unknown state is `warning`, never
 * `success` and never `neutral`: "this build cannot tell you what the check
 * did" is itself something a clinician must not read as uneventful.
 */
export function safetyOutcomeTone(
  code: string,
): "neutral" | "success" | "outline" | "warning" | "destructive" {
  const known = readSafetyOutcome(code);
  return known !== null ? SAFETY_OUTCOME_TONES[known] : "warning";
}

export function describeSafetyReason(code: string): string {
  return oneOf(SAFETY_REASON_CODES, code)
    ? SAFETY_REASON_LABELS[code]
    : `A reason code this build does not know: ${code}.`;
}

export function describeSafetyDetection(value: string): string {
  return oneOf(SAFETY_DETECTIONS, value)
    ? SAFETY_DETECTION_LABELS[value]
    : `A detection state this build does not know: ${value}. It is not a statement that the reply was checked.`;
}

export function describeSafetyIntervention(value: string): string {
  return oneOf(SAFETY_INTERVENTIONS, value)
    ? SAFETY_INTERVENTION_LABELS[value]
    : `An intervention this build does not know: ${value}.`;
}

/**
 * The mechanism's clinician-facing name, or null when this build has none for
 * it — in which case the card shows the producer's own identifier.
 *
 * `Object.hasOwn`, never `in`: `in` walks the prototype chain, so a producer
 * (or a defective record) naming a mechanism `toString` or `constructor` would
 * have resolved to an `Object.prototype` member and handed a FUNCTION to a
 * slot that renders a name.
 */
export function describeSafetyMechanism(mechanism: string): string | null {
  return Object.hasOwn(SAFETY_MECHANISM_LABELS, mechanism)
    ? SAFETY_MECHANISM_LABELS[mechanism as keyof typeof SAFETY_MECHANISM_LABELS]
    : null;
}

/**
 * The safe summary of a restricted technical slot (design §6.2: "Show only a
 * safe label, version/digest and outcome"). The digest and the size describe
 * the record the producer held and withheld; there is no reference to fetch,
 * so nothing here can be opened.
 */
export function describeRestrictedSummary(slot: ContentSlot): string {
  const size =
    slot.byteSize !== null
      ? `${slot.byteSize} byte${slot.byteSize === 1 ? "" : "s"}`
      : "a size the event does not report";
  return `Withheld at source: ${size} of ${slot.encoding ?? "an encoding the event does not report"}, identified by digest only. There is no reference to open and none was requested.`;
}

export function describeRoutingReason(code: string): string {
  return oneOf(ROUTING_REASON_CODES, code)
    ? ROUTING_REASON_LABELS[code]
    : `A reason code this build does not know: ${code}.`;
}

export function describeDisposition(disposition: string): string {
  return oneOf(WHY_NOT_DISPOSITIONS, disposition)
    ? WHY_NOT_DISPOSITION_LABELS[disposition]
    : `A disposition this build does not know: ${disposition}`;
}

export function describeWhyNotReason(code: string): string {
  return oneOf(WHY_NOT_REASON_CODES, code)
    ? WHY_NOT_REASON_LABELS[code]
    : `A reason code this build does not know: ${code}.`;
}

/** A declared missing range, as one sentence. */
export function describeGap(range: MissingRange): string {
  const span =
    range.fromSequence !== null && range.toSequence !== null
      ? range.fromSequence === range.toSequence
        ? `Event ${range.fromSequence}`
        : `Events ${range.fromSequence}–${range.toSequence}`
      : "A range of events";
  const reason = range.reason
    ? OMISSION_REASON_LABELS[range.reason]
    : "reason not reported";
  return `${span} not captured: ${reason}. Any decision inside this range is missing from this view, not absent from the conversation.`;
}

/** "agent · track · phase · step", with the axes the position carries. */
export function describePosition(position: RuntimePosition): string {
  const parts = [
    position.phase !== null ? `phase ${position.phase}` : null,
    position.track !== null ? `track ${position.track}` : null,
    position.agent !== null ? `agent ${position.agent}` : null,
    position.step !== null ? `step ${position.step}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(" · ") : "no position recorded";
}

/** Whether two positions differ on any axis — a transition the turn recorded, not one inferred. */
export function positionsDiffer(a: RuntimePosition, b: RuntimePosition): boolean {
  return a.agent !== b.agent || a.track !== b.track || a.phase !== b.phase || a.step !== b.step;
}

/**
 * The field a safety event's withheld internals are declared under in the
 * envelope's completeness index (R18) — the name the producer's own omission
 * entry carries, so a card can say the withholding is DECLARED and not
 * merely absent.
 */
export const SAFETY_INTERNALS_FIELD = "guardInternals";

/**
 * The one field an evidence reference is read from, per family — the name the
 * producer's completeness index uses when it declares one lost.
 */
export const EVIDENCE_FIELDS = {
  classification: "inputRefs",
  routing: "evidenceRefs",
  why_not: "evidenceRefs",
} as const;

export type StructuredBody =
  | { kind: "structured"; value: unknown }
  | { kind: "text"; text: string };

/**
 * An inline body as the detail read carries it: a JSON body is parsed for a
 * structured rendering, anything else — including a JSON body that does not
 * parse — is text. Text, never markup, is how every body is rendered (design
 * §7.4).
 */
export function readInlineBody(slot: ContentSlot): StructuredBody | null {
  if (slot.state !== "inline" || slot.inline === null) {
    return null;
  }
  if (slot.encoding === "application/json") {
    try {
      return { kind: "structured", value: JSON.parse(slot.inline) as unknown };
    } catch {
      return { kind: "text", text: slot.inline };
    }
  }
  return { kind: "text", text: slot.inline };
}
